// Import all necessary types from shared types

import {
  ApprovalStatus,
  ApiResponse,
  ColumnWithTaskCount,
  Config,
  CreateColumn,
  UpdateColumn,
  ProjectColumn,
  CreateFollowUpAttempt,
  EditorType,
  CreatePrApiRequest,
  CreateTask,
  CreateAndStartTaskRequest,
  CreateTaskAttemptBody,
  CreateTag,
  DirectoryListResponse,
  DirectoryEntry,
  ExecutionProcess,
  ExecutionProcessRepoState,
  GitBranch,
  Project,
  Repo,
  RepoWithTargetBranch,
  CreateProject,
  CreateProjectRepo,
  UpdateRepo,
  SearchMode,
  SearchResult,
  Task,
  TaskRelationships,
  Tag,
  TagSearchParams,
  TaskWithAttemptStatus,
  UpdateProject,
  UpdateTask,
  UpdateTag,
  UserSystemInfo,
  McpServerQuery,
  UpdateMcpServersBody,
  GetMcpServerResponse,
  ImageResponse,
  GitOperationError,
  ApprovalResponse,
  RebaseTaskAttemptRequest,
  ChangeTargetBranchRequest,
  ChangeTargetBranchResponse,
  RenameBranchRequest,
  RenameBranchResponse,
  CheckEditorAvailabilityResponse,
  AvailabilityInfo,
  BaseCodingAgent,
  RunAgentSetupRequest,
  LocalUserInfo,
  UserRole,
  LoginRequest,
  LoginResponse,
  RunAgentSetupResponse,
  GhCliSetupError,
  RunScriptError,
  StatusResponse,
  ListOrganizationsResponse,
  OrganizationMemberWithProfile,
  ListMembersResponse,
  RemoteProjectMembersResponse,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  CreateInvitationRequest,
  CreateInvitationResponse,
  RevokeInvitationRequest,
  UpdateMemberRoleRequest,
  CreateRemoteProjectRequest,
  LinkToExistingRequest,
  UpdateMemberRoleResponse,
  Invitation,
  RemoteProject,
  ListInvitationsResponse,
  OpenEditorResponse,
  OpenEditorRequest,
  PrError,
  Scratch,
  ScratchType,
  CreateScratch,
  UpdateScratch,
  PushError,
  TokenResponse,
  CurrentUserResponse,
  QueueStatus,
  PrCommentsResponse,
  MergeTaskAttemptRequest,
  PushTaskAttemptRequest,
  RepoBranchStatus,
  AbortConflictsRequest,
  Session,
  Workspace,
  StartReviewRequest,
  ReviewError,
  EditorsConfigResponse,
  VerifyVscodeWebResponse,
} from 'shared/types';
import type { WorkspaceWithSession } from '@/types/attempt';
import { createWorkspaceWithSession } from '@/types/attempt';
import { apiLogger } from './logger';

export class ApiError<E = unknown> extends Error {
  public status?: number;
  public error_data?: E;

  constructor(
    message: string,
    public statusCode?: number,
    public response?: Response,
    error_data?: E
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = statusCode;
    this.error_data = error_data;
  }
}

const makeRequest = async (url: string, options: RequestInit = {}) => {
  const headers = new Headers(options.headers ?? {});
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Add session ID if available
  const sessionId = localStorage.getItem('session_id');
  if (sessionId && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${sessionId}`);
  }

  // Log request details
  apiLogger.debug(`${options.method || 'GET'} ${url}`, {
    hasAuth: !!sessionId,
  });

  return fetch(url, {
    ...options,
    headers,
  });
};

export type Ok<T> = { success: true; data: T };
export type Err<E> = { success: false; error: E | undefined; message?: string };

// Result type for endpoints that need typed errors
export type Result<T, E> = Ok<T> | Err<E>;

// Special handler for Result-returning endpoints
const handleApiResponseAsResult = async <T, E>(
  response: Response
): Promise<Result<T, E>> => {
  if (!response.ok) {
    // HTTP error - no structured error data
    let errorMessage = `Request failed with status ${response.status}`;

    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }

    return {
      success: false,
      error: undefined,
      message: errorMessage,
    };
  }

  const result: ApiResponse<T, E> = await response.json();

  if (!result.success) {
    return {
      success: false,
      error: result.error_data || undefined,
      message: result.message || undefined,
    };
  }

  return { success: true, data: result.data as T };
};

export const handleApiResponse = async <T, E = T>(
  response: Response
): Promise<T> => {
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorData: any = null;

    try {
      errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Fallback to status text if JSON parsing fails
      errorMessage = response.statusText || errorMessage;
    }

    apiLogger.error(`${response.status} ${response.url}`, {
      message: errorMessage,
      errorData,
    });
    throw new ApiError<E>(errorMessage, response.status, response);
  }

  // Log successful responses in debug mode
  apiLogger.debug(`${response.status} ${response.url}`);

  if (response.status === 204) {
    return undefined as T;
  }

  const result: ApiResponse<T, E> = await response.json();

  if (!result.success) {
    // Check for error_data first (structured errors), then fall back to message
    if (result.error_data) {
      console.error('[API Error with data]', {
        error_data: result.error_data,
        message: result.message,
        status: response.status,
        response,
        endpoint: response.url,
        timestamp: new Date().toISOString(),
      });
      // Throw a properly typed error with the error data
      throw new ApiError<E>(
        result.message || 'API request failed',
        response.status,
        response,
        result.error_data
      );
    }

    console.error('[API Error]', {
      message: result.message || 'API request failed',
      status: response.status,
      response,
      endpoint: response.url,
      timestamp: new Date().toISOString(),
    });
    throw new ApiError<E>(
      result.message || 'API request failed',
      response.status,
      response
    );
  }

  return result.data as T;
};

// Project Management APIs
export const projectsApi = {
  list: async (): Promise<Project[]> => {
    const response = await makeRequest('/api/projects');
    return handleApiResponse<Project[]>(response);
  },

  create: async (data: CreateProject): Promise<Project> => {
    const response = await makeRequest('/api/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Project>(response);
  },

  update: async (id: string, data: UpdateProject): Promise<Project> => {
    const response = await makeRequest(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Project>(response);
  },

  getRemoteMembers: async (
    projectId: string
  ): Promise<RemoteProjectMembersResponse> => {
    const response = await makeRequest(
      `/api/projects/${projectId}/remote/members`
    );
    return handleApiResponse<RemoteProjectMembersResponse>(response);
  },

  delete: async (id: string): Promise<void> => {
    const response = await makeRequest(`/api/projects/${id}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },

  openEditor: async (
    id: string,
    data: OpenEditorRequest
  ): Promise<OpenEditorResponse> => {
    const response = await makeRequest(`/api/projects/${id}/open-editor`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<OpenEditorResponse>(response);
  },

  searchFiles: async (
    id: string,
    query: string,
    mode?: SearchMode,
    options?: RequestInit
  ): Promise<SearchResult[]> => {
    const modeParam = mode ? `&mode=${encodeURIComponent(mode)}` : '';
    const response = await makeRequest(
      `/api/projects/${id}/search?q=${encodeURIComponent(query)}${modeParam}`,
      options
    );
    return handleApiResponse<SearchResult[]>(response);
  },

  linkToExisting: async (
    localProjectId: string,
    data: LinkToExistingRequest
  ): Promise<Project> => {
    const response = await makeRequest(`/api/projects/${localProjectId}/link`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Project>(response);
  },

  createAndLink: async (
    localProjectId: string,
    data: CreateRemoteProjectRequest
  ): Promise<Project> => {
    const response = await makeRequest(
      `/api/projects/${localProjectId}/link/create`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<Project>(response);
  },

  unlink: async (projectId: string): Promise<Project> => {
    const response = await makeRequest(`/api/projects/${projectId}/link`, {
      method: 'DELETE',
    });
    return handleApiResponse<Project>(response);
  },

  getRepositories: async (projectId: string): Promise<Repo[]> => {
    const response = await makeRequest(
      `/api/projects/${projectId}/repositories`
    );
    return handleApiResponse<Repo[]>(response);
  },

  addRepository: async (
    projectId: string,
    data: CreateProjectRepo
  ): Promise<Repo> => {
    const response = await makeRequest(
      `/api/projects/${projectId}/repositories`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<Repo>(response);
  },

  deleteRepository: async (
    projectId: string,
    repoId: string
  ): Promise<void> => {
    const response = await makeRequest(
      `/api/projects/${projectId}/repositories/${repoId}`,
      {
        method: 'DELETE',
      }
    );
    return handleApiResponse<void>(response);
  },
};

// Task Management APIs
export const tasksApi = {
  getById: async (taskId: string): Promise<Task> => {
    const response = await makeRequest(`/api/tasks/${taskId}`);
    return handleApiResponse<Task>(response);
  },

  create: async (data: CreateTask): Promise<Task> => {
    const response = await makeRequest(`/api/tasks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Task>(response);
  },

  createAndStart: async (
    data: CreateAndStartTaskRequest
  ): Promise<TaskWithAttemptStatus> => {
    const response = await makeRequest(`/api/tasks/create-and-start`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<TaskWithAttemptStatus>(response);
  },

  update: async (taskId: string, data: UpdateTask): Promise<Task> => {
    const response = await makeRequest(`/api/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Task>(response);
  },

  delete: async (taskId: string): Promise<void> => {
    const response = await makeRequest(`/api/tasks/${taskId}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },
};

// Sessions API
export const sessionsApi = {
  getByWorkspace: async (workspaceId: string): Promise<Session[]> => {
    const response = await makeRequest(
      `/api/sessions?workspace_id=${workspaceId}`
    );
    return handleApiResponse<Session[]>(response);
  },

  getById: async (sessionId: string): Promise<Session> => {
    const response = await makeRequest(`/api/sessions/${sessionId}`);
    return handleApiResponse<Session>(response);
  },

  create: async (data: {
    workspace_id: string;
    executor?: string;
  }): Promise<Session> => {
    const response = await makeRequest('/api/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Session>(response);
  },

  followUp: async (
    sessionId: string,
    data: CreateFollowUpAttempt
  ): Promise<ExecutionProcess> => {
    const response = await makeRequest(`/api/sessions/${sessionId}/follow-up`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<ExecutionProcess>(response);
  },

  startReview: async (
    sessionId: string,
    data: StartReviewRequest
  ): Promise<ExecutionProcess> => {
    const response = await makeRequest(`/api/sessions/${sessionId}/review`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<ExecutionProcess, ReviewError>(response);
  },
};

// Task Attempts APIs
export const attemptsApi = {
  getChildren: async (attemptId: string): Promise<TaskRelationships> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/children`
    );
    return handleApiResponse<TaskRelationships>(response);
  },

  getAll: async (taskId: string): Promise<Workspace[]> => {
    const response = await makeRequest(`/api/task-attempts?task_id=${taskId}`);
    return handleApiResponse<Workspace[]>(response);
  },

  /** Get all workspaces across all tasks (newest first) */
  getAllWorkspaces: async (): Promise<Workspace[]> => {
    const response = await makeRequest('/api/task-attempts');
    return handleApiResponse<Workspace[]>(response);
  },

  /** Get total count of workspaces */
  getCount: async (): Promise<number> => {
    const response = await makeRequest('/api/task-attempts/count');
    return handleApiResponse<number>(response);
  },

  get: async (attemptId: string): Promise<Workspace> => {
    const response = await makeRequest(`/api/task-attempts/${attemptId}`);
    return handleApiResponse<Workspace>(response);
  },

  update: async (
    attemptId: string,
    data: { archived?: boolean; pinned?: boolean; name?: string }
  ): Promise<Workspace> => {
    const response = await makeRequest(`/api/task-attempts/${attemptId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Workspace>(response);
  },

  /** Get workspace with latest session */
  getWithSession: async (attemptId: string): Promise<WorkspaceWithSession> => {
    const [workspace, sessions] = await Promise.all([
      attemptsApi.get(attemptId),
      sessionsApi.getByWorkspace(attemptId),
    ]);
    return createWorkspaceWithSession(workspace, sessions[0]);
  },

  create: async (data: CreateTaskAttemptBody): Promise<Workspace> => {
    const response = await makeRequest(`/api/task-attempts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Workspace>(response);
  },

  stop: async (attemptId: string): Promise<void> => {
    const response = await makeRequest(`/api/task-attempts/${attemptId}/stop`, {
      method: 'POST',
    });
    return handleApiResponse<void>(response);
  },

  delete: async (attemptId: string): Promise<void> => {
    const response = await makeRequest(`/api/task-attempts/${attemptId}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },

  searchFiles: async (
    workspaceId: string,
    query: string,
    mode?: string
  ): Promise<SearchResult[]> => {
    const modeParam = mode ? `&mode=${encodeURIComponent(mode)}` : '';
    const response = await makeRequest(
      `/api/task-attempts/${workspaceId}/search?q=${encodeURIComponent(query)}${modeParam}`
    );
    return handleApiResponse<SearchResult[]>(response);
  },

  runAgentSetup: async (
    attemptId: string,
    data: RunAgentSetupRequest
  ): Promise<RunAgentSetupResponse> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/run-agent-setup`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<RunAgentSetupResponse>(response);
  },

  openEditor: async (
    attemptId: string,
    data: OpenEditorRequest
  ): Promise<OpenEditorResponse> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/open-editor`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<OpenEditorResponse>(response);
  },

  getBranchStatus: async (attemptId: string): Promise<RepoBranchStatus[]> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/branch-status`
    );
    return handleApiResponse<RepoBranchStatus[]>(response);
  },

  getRepos: async (attemptId: string): Promise<RepoWithTargetBranch[]> => {
    const response = await makeRequest(`/api/task-attempts/${attemptId}/repos`);
    return handleApiResponse<RepoWithTargetBranch[]>(response);
  },

  getFirstUserMessage: async (attemptId: string): Promise<string | null> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/first-message`
    );
    return handleApiResponse<string | null>(response);
  },

  merge: async (
    attemptId: string,
    data: MergeTaskAttemptRequest
  ): Promise<void> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/merge`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<void>(response);
  },

  push: async (
    attemptId: string,
    data: PushTaskAttemptRequest
  ): Promise<Result<void, PushError>> => {
    const response = await makeRequest(`/api/task-attempts/${attemptId}/push`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponseAsResult<void, PushError>(response);
  },

  forcePush: async (
    attemptId: string,
    data: PushTaskAttemptRequest
  ): Promise<Result<void, PushError>> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/push/force`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponseAsResult<void, PushError>(response);
  },

  rebase: async (
    attemptId: string,
    data: RebaseTaskAttemptRequest
  ): Promise<Result<void, GitOperationError>> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/rebase`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponseAsResult<void, GitOperationError>(response);
  },

  change_target_branch: async (
    attemptId: string,
    data: ChangeTargetBranchRequest
  ): Promise<ChangeTargetBranchResponse> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/change-target-branch`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<ChangeTargetBranchResponse>(response);
  },

  renameBranch: async (
    attemptId: string,
    newBranchName: string
  ): Promise<RenameBranchResponse> => {
    const payload: RenameBranchRequest = {
      new_branch_name: newBranchName,
    };
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/rename-branch`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    return handleApiResponse<RenameBranchResponse>(response);
  },

  abortConflicts: async (
    attemptId: string,
    data: AbortConflictsRequest
  ): Promise<void> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/conflicts/abort`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<void>(response);
  },

  createPR: async (
    attemptId: string,
    data: CreatePrApiRequest
  ): Promise<Result<string, PrError>> => {
    const response = await makeRequest(`/api/task-attempts/${attemptId}/pr`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponseAsResult<string, PrError>(response);
  },

  startDevServer: async (attemptId: string): Promise<ExecutionProcess[]> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/start-dev-server`,
      {
        method: 'POST',
      }
    );
    return handleApiResponse<ExecutionProcess[]>(response);
  },

  setupGhCli: async (attemptId: string): Promise<ExecutionProcess> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/gh-cli-setup`,
      {
        method: 'POST',
      }
    );
    return handleApiResponse<ExecutionProcess, GhCliSetupError>(response);
  },

  runSetupScript: async (
    attemptId: string
  ): Promise<Result<ExecutionProcess, RunScriptError>> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/run-setup-script`,
      {
        method: 'POST',
      }
    );
    return handleApiResponseAsResult<ExecutionProcess, RunScriptError>(
      response
    );
  },

  runCleanupScript: async (
    attemptId: string
  ): Promise<Result<ExecutionProcess, RunScriptError>> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/run-cleanup-script`,
      {
        method: 'POST',
      }
    );
    return handleApiResponseAsResult<ExecutionProcess, RunScriptError>(
      response
    );
  },

  getPrComments: async (
    attemptId: string,
    repoId: string
  ): Promise<PrCommentsResponse> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/pr/comments?repo_id=${encodeURIComponent(repoId)}`
    );
    return handleApiResponse<PrCommentsResponse>(response);
  },

  /** Mark all coding agent turns for a workspace as seen */
  markSeen: async (attemptId: string): Promise<void> => {
    const response = await makeRequest(
      `/api/task-attempts/${attemptId}/mark-seen`,
      {
        method: 'PUT',
      }
    );
    return handleApiResponse<void>(response);
  },
};

// Execution Process APIs
export const executionProcessesApi = {
  getDetails: async (processId: string): Promise<ExecutionProcess> => {
    const response = await makeRequest(`/api/execution-processes/${processId}`);
    return handleApiResponse<ExecutionProcess>(response);
  },

  getRepoStates: async (
    processId: string
  ): Promise<ExecutionProcessRepoState[]> => {
    const response = await makeRequest(
      `/api/execution-processes/${processId}/repo-states`
    );
    return handleApiResponse<ExecutionProcessRepoState[]>(response);
  },

  stopExecutionProcess: async (processId: string): Promise<void> => {
    const response = await makeRequest(
      `/api/execution-processes/${processId}/stop`,
      {
        method: 'POST',
      }
    );
    return handleApiResponse<void>(response);
  },
};

// File System APIs
export const fileSystemApi = {
  list: async (path?: string): Promise<DirectoryListResponse> => {
    const queryParam = path ? `?path=${encodeURIComponent(path)}` : '';
    const response = await makeRequest(
      `/api/filesystem/directory${queryParam}`
    );
    return handleApiResponse<DirectoryListResponse>(response);
  },

  listGitRepos: async (path?: string): Promise<DirectoryEntry[]> => {
    const queryParam = path ? `?path=${encodeURIComponent(path)}` : '';
    const response = await makeRequest(
      `/api/filesystem/git-repos${queryParam}`
    );
    return handleApiResponse<DirectoryEntry[]>(response);
  },
};

// Repo APIs
export const repoApi = {
  list: async (): Promise<Repo[]> => {
    const response = await makeRequest('/api/repos');
    return handleApiResponse<Repo[]>(response);
  },

  getById: async (repoId: string): Promise<Repo> => {
    const response = await makeRequest(`/api/repos/${repoId}`);
    return handleApiResponse<Repo>(response);
  },

  update: async (repoId: string, data: UpdateRepo): Promise<Repo> => {
    const response = await makeRequest(`/api/repos/${repoId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Repo>(response);
  },

  register: async (data: {
    path: string;
    display_name?: string;
  }): Promise<Repo> => {
    const response = await makeRequest('/api/repos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Repo>(response);
  },

  getBranches: async (repoId: string): Promise<GitBranch[]> => {
    const response = await makeRequest(`/api/repos/${repoId}/branches`);
    return handleApiResponse<GitBranch[]>(response);
  },

  init: async (data: {
    parent_path: string;
    folder_name: string;
  }): Promise<Repo> => {
    const response = await makeRequest('/api/repos/init', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Repo>(response);
  },

  getBatch: async (ids: string[]): Promise<Repo[]> => {
    const response = await makeRequest('/api/repos/batch', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return handleApiResponse<Repo[]>(response);
  },

  openEditor: async (
    repoId: string,
    data: OpenEditorRequest
  ): Promise<OpenEditorResponse> => {
    const response = await makeRequest(`/api/repos/${repoId}/open-editor`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<OpenEditorResponse>(response);
  },

  searchFiles: async (
    repoId: string,
    query: string,
    mode?: SearchMode,
    options?: RequestInit
  ): Promise<SearchResult[]> => {
    const modeParam = mode ? `&mode=${encodeURIComponent(mode)}` : '';
    const response = await makeRequest(
      `/api/repos/${repoId}/search?q=${encodeURIComponent(query)}${modeParam}`,
      options
    );
    return handleApiResponse<SearchResult[]>(response);
  },
};

// Config APIs (backwards compatible)
export const configApi = {
  getConfig: async (): Promise<UserSystemInfo> => {
    const response = await makeRequest('/api/info', { cache: 'no-store' });
    return handleApiResponse<UserSystemInfo>(response);
  },
  saveConfig: async (config: Config): Promise<Config> => {
    const response = await makeRequest('/api/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
    return handleApiResponse<Config>(response);
  },
  checkEditorAvailability: async (
    editorType: EditorType
  ): Promise<CheckEditorAvailabilityResponse> => {
    const response = await makeRequest(
      `/api/editors/check-availability?editor_type=${encodeURIComponent(editorType)}`
    );
    return handleApiResponse<CheckEditorAvailabilityResponse>(response);
  },
  checkAgentAvailability: async (
    agent: BaseCodingAgent
  ): Promise<AvailabilityInfo> => {
    const response = await makeRequest(
      `/api/agents/check-availability?executor=${encodeURIComponent(agent)}`
    );
    return handleApiResponse<AvailabilityInfo>(response);
  },
};

// Task Tags APIs (all tags are global)
export const tagsApi = {
  list: async (params?: TagSearchParams): Promise<Tag[]> => {
    const queryParam = params?.search
      ? `?search=${encodeURIComponent(params.search)}`
      : '';
    const response = await makeRequest(`/api/tags${queryParam}`);
    return handleApiResponse<Tag[]>(response);
  },

  create: async (data: CreateTag): Promise<Tag> => {
    const response = await makeRequest('/api/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Tag>(response);
  },

  update: async (tagId: string, data: UpdateTag): Promise<Tag> => {
    const response = await makeRequest(`/api/tags/${tagId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Tag>(response);
  },

  delete: async (tagId: string): Promise<void> => {
    const response = await makeRequest(`/api/tags/${tagId}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },
};

// MCP Servers APIs
export const mcpServersApi = {
  load: async (query: McpServerQuery): Promise<GetMcpServerResponse> => {
    const params = new URLSearchParams(query);
    const response = await makeRequest(`/api/mcp-config?${params.toString()}`);
    return handleApiResponse<GetMcpServerResponse>(response);
  },
  save: async (
    query: McpServerQuery,
    data: UpdateMcpServersBody
  ): Promise<void> => {
    const params = new URLSearchParams(query);
    // params.set('profile', profile);
    const response = await makeRequest(`/api/mcp-config?${params.toString()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error('[API Error] Failed to save MCP servers', {
        message: errorData.message,
        status: response.status,
        response,
        timestamp: new Date().toISOString(),
      });
      throw new ApiError(
        errorData.message || 'Failed to save MCP servers',
        response.status,
        response
      );
    }
  },
};

// Profiles API
export const profilesApi = {
  load: async (): Promise<{ content: string; path: string }> => {
    const response = await makeRequest('/api/profiles');
    return handleApiResponse<{ content: string; path: string }>(response);
  },
  save: async (content: string): Promise<string> => {
    const response = await makeRequest('/api/profiles', {
      method: 'PUT',
      body: content,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return handleApiResponse<string>(response);
  },
};

// Images API
export const imagesApi = {
  upload: async (file: File): Promise<ImageResponse> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch('/api/images/upload', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(
        `Failed to upload image: ${errorText}`,
        response.status,
        response
      );
    }

    return handleApiResponse<ImageResponse>(response);
  },

  uploadForTask: async (taskId: string, file: File): Promise<ImageResponse> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`/api/images/task/${taskId}/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(
        `Failed to upload image: ${errorText}`,
        response.status,
        response
      );
    }

    return handleApiResponse<ImageResponse>(response);
  },

  /**
   * Upload an image for a task attempt and immediately copy it to the container.
   * Returns the image with a file_path that can be used in markdown.
   */
  uploadForAttempt: async (
    attemptId: string,
    file: File
  ): Promise<ImageResponse> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(
      `/api/task-attempts/${attemptId}/images/upload`,
      {
        method: 'POST',
        body: formData,
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(
        `Failed to upload image: ${errorText}`,
        response.status,
        response
      );
    }

    return handleApiResponse<ImageResponse>(response);
  },

  delete: async (imageId: string): Promise<void> => {
    const response = await makeRequest(`/api/images/${imageId}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },

  getTaskImages: async (taskId: string): Promise<ImageResponse[]> => {
    const response = await makeRequest(`/api/images/task/${taskId}`);
    return handleApiResponse<ImageResponse[]>(response);
  },

  getImageUrl: (imageId: string): string => {
    return `/api/images/${imageId}/file`;
  },
};

// Approval API
export const approvalsApi = {
  respond: async (
    approvalId: string,
    payload: ApprovalResponse,
    signal?: AbortSignal
  ): Promise<ApprovalStatus> => {
    const res = await makeRequest(`/api/approvals/${approvalId}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });

    return handleApiResponse<ApprovalStatus>(res);
  },
};

// OAuth API
export const oauthApi = {
  handoffInit: async (
    provider: string,
    returnTo: string
  ): Promise<{ handoff_id: string; authorize_url: string }> => {
    const response = await makeRequest('/api/auth/handoff/init', {
      method: 'POST',
      body: JSON.stringify({ provider, return_to: returnTo }),
    });
    return handleApiResponse<{ handoff_id: string; authorize_url: string }>(
      response
    );
  },

  status: async (): Promise<StatusResponse> => {
    const response = await makeRequest('/api/auth/status', {
      cache: 'no-store',
    });
    return handleApiResponse<StatusResponse>(response);
  },

  logout: async (): Promise<void> => {
    const response = await makeRequest('/api/auth/logout', {
      method: 'POST',
    });
    if (!response.ok) {
      throw new ApiError(
        `Logout failed with status ${response.status}`,
        response.status,
        response
      );
    }

    localStorage.removeItem('session_id');
    localStorage.removeItem('user');
    window.dispatchEvent(new CustomEvent('vk-session-logout'));
  },

  /** Returns the current access token for the remote server (auto-refreshes if needed) */
  getToken: async (): Promise<TokenResponse | null> => {
    const response = await makeRequest('/api/auth/token');
    if (!response.ok) return null;
    return handleApiResponse<TokenResponse>(response);
  },

  /** Returns the user ID of the currently authenticated user */
  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    const response = await makeRequest('/api/auth/user');
    return handleApiResponse<CurrentUserResponse>(response);
  },
};

// Local Auth API
export const localAuthApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await makeRequest('/api/auth/local/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const result = await handleApiResponse<LoginResponse>(response);
    // Store session ID
    localStorage.setItem('session_id', result.session_id);
    localStorage.setItem('user', JSON.stringify(result.user));
    return result;
  },

  logout: async (): Promise<void> => {
    const response = await makeRequest('/api/auth/local/logout', {
      method: 'POST',
    });
    if (!response.ok) {
      throw new ApiError(
        `Logout failed with status ${response.status}`,
        response.status,
        response
      );
    }
    localStorage.removeItem('session_id');
    localStorage.removeItem('user');
    // Clear all cached queries to prevent stale data on next login
    window.dispatchEvent(new CustomEvent('vk-session-logout'));
  },

  getCurrentUser: async (): Promise<LocalUserInfo | null> => {
    try {
      const response = await makeRequest('/api/auth/local/me');
      return handleApiResponse<LocalUserInfo>(response);
    } catch (error) {
      // Not logged in
      return null;
    }
  },
};

// Organizations API
export const organizationsApi = {
  getMembers: async (
    orgId: string
  ): Promise<OrganizationMemberWithProfile[]> => {
    const response = await makeRequest(`/api/organizations/${orgId}/members`);
    const result = await handleApiResponse<ListMembersResponse>(response);
    return result.members;
  },

  getUserOrganizations: async (): Promise<ListOrganizationsResponse> => {
    const response = await makeRequest('/api/organizations');
    return handleApiResponse<ListOrganizationsResponse>(response);
  },

  getProjects: async (orgId: string): Promise<RemoteProject[]> => {
    const response = await makeRequest(`/api/organizations/${orgId}/projects`);
    return handleApiResponse<RemoteProject[]>(response);
  },

  createOrganization: async (
    data: CreateOrganizationRequest
  ): Promise<CreateOrganizationResponse> => {
    const response = await makeRequest('/api/organizations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleApiResponse<CreateOrganizationResponse>(response);
  },

  createInvitation: async (
    orgId: string,
    data: CreateInvitationRequest
  ): Promise<CreateInvitationResponse> => {
    const response = await makeRequest(
      `/api/organizations/${orgId}/invitations`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<CreateInvitationResponse>(response);
  },

  removeMember: async (orgId: string, userId: string): Promise<void> => {
    const response = await makeRequest(
      `/api/organizations/${orgId}/members/${userId}`,
      {
        method: 'DELETE',
      }
    );
    return handleApiResponse<void>(response);
  },

  updateMemberRole: async (
    orgId: string,
    userId: string,
    data: UpdateMemberRoleRequest
  ): Promise<UpdateMemberRoleResponse> => {
    const response = await makeRequest(
      `/api/organizations/${orgId}/members/${userId}/role`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<UpdateMemberRoleResponse>(response);
  },

  listInvitations: async (orgId: string): Promise<Invitation[]> => {
    const response = await makeRequest(
      `/api/organizations/${orgId}/invitations`
    );
    const result = await handleApiResponse<ListInvitationsResponse>(response);
    return result.invitations;
  },

  revokeInvitation: async (
    orgId: string,
    invitationId: string
  ): Promise<void> => {
    const body: RevokeInvitationRequest = { invitation_id: invitationId };
    const response = await makeRequest(
      `/api/organizations/${orgId}/invitations/revoke`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    return handleApiResponse<void>(response);
  },

  deleteOrganization: async (orgId: string): Promise<void> => {
    const response = await makeRequest(`/api/organizations/${orgId}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },
};

// Scratch API
export const scratchApi = {
  create: async (
    scratchType: ScratchType,
    id: string,
    data: CreateScratch
  ): Promise<Scratch> => {
    const response = await makeRequest(`/api/scratch/${scratchType}/${id}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<Scratch>(response);
  },

  get: async (scratchType: ScratchType, id: string): Promise<Scratch> => {
    const response = await makeRequest(`/api/scratch/${scratchType}/${id}`);
    return handleApiResponse<Scratch>(response);
  },

  update: async (
    scratchType: ScratchType,
    id: string,
    data: UpdateScratch
  ): Promise<void> => {
    const response = await makeRequest(`/api/scratch/${scratchType}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<void>(response);
  },

  delete: async (scratchType: ScratchType, id: string): Promise<void> => {
    const response = await makeRequest(`/api/scratch/${scratchType}/${id}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },

  getStreamUrl: (scratchType: ScratchType, id: string): string =>
    `/api/scratch/${scratchType}/${id}/stream/ws`,
};

// Users API (Admin only)
export const usersApi = {
  list: async (): Promise<LocalUserInfo[]> => {
    const response = await makeRequest('/api/users');
    return handleApiResponse<LocalUserInfo[]>(response);
  },

  create: async (
    data: LoginRequest & { role: UserRole }
  ): Promise<LocalUserInfo> => {
    const response = await makeRequest('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<LocalUserInfo>(response);
  },

  update: async (
    userId: number,
    data: { username?: string; role?: UserRole; custom_role_id?: number }
  ): Promise<LocalUserInfo> => {
    const response = await makeRequest(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<LocalUserInfo>(response);
  },

  resetPassword: async (userId: number, password: string): Promise<void> => {
    const response = await makeRequest(`/api/users/${userId}/reset-password`, {
      method: 'PATCH',
      body: JSON.stringify({ password }),
    });
    return handleApiResponse<void>(response);
  },

  delete: async (userId: number): Promise<void> => {
    const response = await makeRequest(`/api/users/${userId}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },
};

// Roles & Permissions API
export interface RolePermissions {
  role: UserRole;
  permissions: string[];
}
export interface PermissionInfo {
  code: string;
  category: string;
  description: string;
}
export interface RolesResponse {
  roles: RolePermissions[];
  all_permissions: PermissionInfo[];
  custom_roles: CustomRoleInfo[];
}
export interface CustomRoleInfo {
  id: number;
  name: string;
  display_name: string;
  description: string;
  color: string;
  hierarchy_level: number;
  permissions: string[];
}
export interface UserPermissionsResponse {
  role: UserRole;
  permissions: string[];
  custom_role?: CustomRoleInfo;
}

export const rolesApi = {
  getRoles: async (): Promise<RolesResponse> => {
    const response = await makeRequest('/api/roles');
    return handleApiResponse<RolesResponse>(response);
  },
  getMyPermissions: async (): Promise<UserPermissionsResponse> => {
    const response = await makeRequest('/api/my-permissions');
    return handleApiResponse<UserPermissionsResponse>(response);
  },
  updateRolePermissions: async (
    role: UserRole,
    permissions: string[]
  ): Promise<RolePermissions> => {
    const response = await makeRequest(`/api/roles/${role}`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
    return handleApiResponse<RolePermissions>(response);
  },
  // Custom roles CRUD
  createCustomRole: async (data: {
    name: string;
    display_name: string;
    description?: string;
    color?: string;
    hierarchy_level?: number;
    permissions?: string[];
  }): Promise<CustomRoleInfo> => {
    const response = await makeRequest('/api/custom-roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<CustomRoleInfo>(response);
  },
  updateCustomRole: async (
    id: number,
    data: {
      display_name?: string;
      description?: string;
      color?: string;
      hierarchy_level?: number;
      permissions?: string[];
    }
  ): Promise<CustomRoleInfo> => {
    const response = await makeRequest(`/api/custom-roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<CustomRoleInfo>(response);
  },
  deleteCustomRole: async (id: number): Promise<void> => {
    const response = await makeRequest(`/api/custom-roles/${id}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },
};

// User Sync API (PAI ↔ OpenProject user linking, Admin only)
export interface OpUserInfo {
  id: number;
  login: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
}

export interface UserSyncMappingInfo {
  id: number;
  pai_user_id: number;
  op_user_id: number;
  op_email: string;
  op_name: string;
  synced_at: string;
}

export const userSyncApi = {
  /** List all OpenProject users (for dropdown selection) */
  listOpUsers: async (): Promise<OpUserInfo[]> => {
    const response = await makeRequest('/api/user-sync/op-users');
    return handleApiResponse<OpUserInfo[]>(response);
  },

  /** List all current PAI ↔ OP user mappings */
  getMappings: async (): Promise<UserSyncMappingInfo[]> => {
    const response = await makeRequest('/api/user-sync/mappings');
    return handleApiResponse<UserSyncMappingInfo[]>(response);
  },

  /** Link a PAI user to an OpenProject user */
  link: async (
    userId: number,
    opUserId: number,
    opEmail: string,
    opName: string
  ): Promise<UserSyncMappingInfo> => {
    const response = await makeRequest(`/api/users/${userId}/link-ops`, {
      method: 'POST',
      body: JSON.stringify({
        op_user_id: opUserId,
        op_email: opEmail,
        op_name: opName,
      }),
    });
    return handleApiResponse<UserSyncMappingInfo>(response);
  },

  /** Unlink a PAI user from OpenProject */
  unlink: async (userId: number): Promise<void> => {
    const response = await makeRequest(`/api/users/${userId}/link-ops`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },
};

// Editors Config API (Admin only)
export const editorsConfigApi = {
  list: async (): Promise<EditorsConfigResponse[]> => {
    const response = await makeRequest('/api/editors-config');
    return handleApiResponse<EditorsConfigResponse[]>(response);
  },

  update: async (
    key: string,
    value: string
  ): Promise<EditorsConfigResponse> => {
    const response = await makeRequest('/api/editors-config', {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    });
    return handleApiResponse<EditorsConfigResponse>(response);
  },
};

// VS Code Web API
export const vscodeWebApi = {
  verify: async (workspaceId: string): Promise<VerifyVscodeWebResponse> => {
    const response = await makeRequest('/api/vscode-web/verify', {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId }),
    });
    return handleApiResponse<VerifyVscodeWebResponse>(response);
  },

  verifyWithCookie: async (
    workspaceId: string
  ): Promise<VerifyVscodeWebResponse> => {
    const response = await makeRequest('/api/vscode-web/verify-with-cookie', {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId }),
    });
    return handleApiResponse<VerifyVscodeWebResponse>(response);
  },

  verifyProjectWithCookie: async (
    projectId: string
  ): Promise<VerifyVscodeWebResponse> => {
    const response = await makeRequest(
      '/api/vscode-web/verify-project-with-cookie',
      {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId }),
      }
    );
    return handleApiResponse<VerifyVscodeWebResponse>(response);
  },
};

// Project Members API (Admin only)
export const projectMembersApi = {
  getMembers: async (projectId: string): Promise<LocalUserInfo[]> => {
    const response = await makeRequest(`/api/projects/${projectId}/members`);
    const result = await handleApiResponse<{ members: LocalUserInfo[] }>(
      response
    );
    return result.members;
  },

  assignMember: async (projectId: string, userId: number): Promise<void> => {
    const response = await makeRequest(`/api/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
    return handleApiResponse<void>(response);
  },

  removeMember: async (projectId: string, userId: number): Promise<void> => {
    const response = await makeRequest(
      `/api/projects/${projectId}/members/${userId}`,
      {
        method: 'DELETE',
      }
    );
    return handleApiResponse<void>(response);
  },

  getUserProjects: async (userId: number): Promise<string[]> => {
    const response = await makeRequest(`/api/users/${userId}/projects`);
    return handleApiResponse<string[]>(response);
  },
};

// Queue API for session follow-up messages
export const queueApi = {
  /**
   * Queue a follow-up message to be executed when current execution finishes
   */
  queue: async (
    sessionId: string,
    data: { message: string; variant: string | null }
  ): Promise<QueueStatus> => {
    const response = await makeRequest(`/api/sessions/${sessionId}/queue`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<QueueStatus>(response);
  },

  /**
   * Cancel a queued follow-up message
   */
  cancel: async (sessionId: string): Promise<QueueStatus> => {
    const response = await makeRequest(`/api/sessions/${sessionId}/queue`, {
      method: 'DELETE',
    });
    return handleApiResponse<QueueStatus>(response);
  },

  /**
   * Get the current queue status for a session
   */
  getStatus: async (sessionId: string): Promise<QueueStatus> => {
    const response = await makeRequest(`/api/sessions/${sessionId}/queue`);
    return handleApiResponse<QueueStatus>(response);
  },
};

// ── OpenProject Sync Types & API ──────────────────────────────

export interface OpConfig {
  id: number;
  base_url: string;
  api_token: string;
  enabled: number;
  status_mapping: string;
  vk_base_url: string;
  slack_signing_secret: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateOpConfig {
  base_url?: string;
  api_token?: string;
  enabled?: boolean;
  status_mapping?: string;
  vk_base_url?: string;
  slack_signing_secret?: string;
}

export interface OpSyncMapping {
  id: number;
  vk_entity_type: string;
  vk_entity_id: string;
  op_entity_type: string;
  op_entity_id: number;
  op_identifier: string | null;
  synced_at: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
}

export interface OpProjectItem {
  id: number;
  identifier: string;
  name: string;
  active: boolean;
}

export interface ProjectSyncStatus {
  id: string;
  name: string;
  synced: boolean;
  op_project_id: number | null;
  op_identifier: string | null;
  synced_at: string | null;
}

export interface ImportProjectResponse {
  project_id: string;
  project_name: string;
  tasks_imported: number;
  tasks_skipped: number;
}

export interface ResyncBoardResponse {
  project_id: string;
  project_name: string;
  columns_synced: number;
  statuses_created: number;
  board_rebuilt: boolean;
  message: string;
}

export const opSyncApi = {
  getConfig: async (): Promise<OpConfig> => {
    const response = await makeRequest('/api/op-sync/config');
    return handleApiResponse<OpConfig>(response);
  },

  updateConfig: async (data: UpdateOpConfig): Promise<OpConfig> => {
    const response = await makeRequest('/api/op-sync/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<OpConfig>(response);
  },

  testConnection: async (params?: {
    base_url?: string;
    api_token?: string;
  }): Promise<TestConnectionResponse> => {
    const response = await makeRequest('/api/op-sync/test', {
      method: 'POST',
      body: params ? JSON.stringify(params) : undefined,
    });
    return handleApiResponse<TestConnectionResponse>(response);
  },

  getMappings: async (): Promise<OpSyncMapping[]> => {
    const response = await makeRequest('/api/op-sync/mappings');
    return handleApiResponse<OpSyncMapping[]>(response);
  },

  listOpProjects: async (): Promise<OpProjectItem[]> => {
    const response = await makeRequest('/api/op-sync/op-projects');
    return handleApiResponse<OpProjectItem[]>(response);
  },

  listProjectSyncStatus: async (): Promise<ProjectSyncStatus[]> => {
    const response = await makeRequest('/api/op-sync/projects');
    return handleApiResponse<ProjectSyncStatus[]>(response);
  },

  syncProject: async (projectId: string): Promise<ProjectSyncStatus> => {
    const response = await makeRequest(
      `/api/op-sync/sync-project/${projectId}`,
      {
        method: 'POST',
      }
    );
    return handleApiResponse<ProjectSyncStatus>(response);
  },

  unsyncProject: async (projectId: string): Promise<ProjectSyncStatus> => {
    const response = await makeRequest(
      `/api/op-sync/sync-project/${projectId}`,
      {
        method: 'DELETE',
      }
    );
    return handleApiResponse<ProjectSyncStatus>(response);
  },

  /** Import an OpenProject project into PAI (creates project + tasks) */
  importProject: async (opProjectId: number): Promise<ImportProjectResponse> => {
    const response = await makeRequest(`/api/op-sync/import-project/${opProjectId}`, {
      method: 'POST',
    });
    return handleApiResponse<ImportProjectResponse>(response);
  },

  /** Resync the OP Kanban board for a synced project to match VK columns */
  resyncBoard: async (projectId: string): Promise<ResyncBoardResponse> => {
    const response = await makeRequest(`/api/op-sync/resync-board/${projectId}`, {
      method: 'POST',
    });
    return handleApiResponse<ResyncBoardResponse>(response);
  },
};

// ── Notification Channels Types & API ─────────────────────────

export interface NotificationChannel {
  id: number;
  channel_type: string;
  target_type: string;
  target_name: string;
  webhook_url: string;
  bot_token: string;
  chat_id: string;
  enabled: number;
  notify_status_change: number;
  notify_errors: number;
  created_at: string;
  updated_at: string;
}

export interface UpsertNotificationChannel {
  target_type?: string;
  target_name?: string;
  webhook_url?: string;
  bot_token?: string;
  chat_id?: string;
  enabled?: boolean;
  notify_status_change?: boolean;
  notify_errors?: boolean;
}

export interface TestNotificationResponse {
  success: boolean;
  message: string;
}

export const notificationChannelsApi = {
  list: async (): Promise<NotificationChannel[]> => {
    const response = await makeRequest('/api/notifications/channels');
    return handleApiResponse<NotificationChannel[]>(response);
  },

  upsert: async (
    channelType: string,
    data: UpsertNotificationChannel
  ): Promise<NotificationChannel> => {
    const response = await makeRequest(
      `/api/notifications/channels/${channelType}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return handleApiResponse<NotificationChannel>(response);
  },

  test: async (id: number): Promise<TestNotificationResponse> => {
    const response = await makeRequest(
      `/api/notifications/channels/${id}/test`,
      {
        method: 'POST',
      }
    );
    return handleApiResponse<TestNotificationResponse>(response);
  },

  delete: async (id: number): Promise<void> => {
    const response = await makeRequest(`/api/notifications/channels/${id}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },

  slackLookup: async (botToken: string): Promise<SlackLookupResponse> => {
    const response = await makeRequest(
      '/api/notifications/channels/slack/lookup',
      {
        method: 'POST',
        body: JSON.stringify({ bot_token: botToken }),
      }
    );
    return handleApiResponse<SlackLookupResponse>(response);
  },
};

// ── Slack Lookup Types ────────────────────────────────────────

export interface SlackChannelItem {
  id: string;
  name: string;
  is_private: boolean;
  num_members: number | null;
}

export interface SlackUserItem {
  id: string;
  name: string;
  real_name: string;
  is_bot: boolean;
}

export interface SlackLookupResponse {
  channels: SlackChannelItem[];
  users: SlackUserItem[];
}

// ── User Slack Mapping ────────────────────────────────────────

export interface UserSlackMapping {
  id: number;
  username: string;
  role: string;
  slack_user_id: string;
}

export const userSlackMappingApi = {
  list: async (): Promise<UserSlackMapping[]> => {
    const response = await makeRequest(
      '/api/notifications/user-slack-mapping',
      {
        method: 'GET',
      }
    );
    return handleApiResponse<UserSlackMapping[]>(response);
  },

  update: async (userId: number, slackUserId: string): Promise<void> => {
    const response = await makeRequest(
      '/api/notifications/user-slack-mapping',
      {
        method: 'PUT',
        body: JSON.stringify({ user_id: userId, slack_user_id: slackUserId }),
      }
    );
    return handleApiResponse<void>(response);
  },
};

// ── Project Columns API ──────────────────────────────────────────

export const columnsApi = {
  list: async (projectId: string): Promise<ColumnWithTaskCount[]> => {
    const response = await makeRequest(`/api/projects/${projectId}/columns`);
    return handleApiResponse<ColumnWithTaskCount[]>(response);
  },

  create: async (
    projectId: string,
    data: CreateColumn
  ): Promise<ProjectColumn> => {
    const response = await makeRequest(`/api/projects/${projectId}/columns`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse<ProjectColumn>(response);
  },

  update: async (
    columnId: string,
    data: UpdateColumn
  ): Promise<ProjectColumn> => {
    const response = await makeRequest(`/api/columns/${columnId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleApiResponse<ProjectColumn>(response);
  },

  delete: async (columnId: string): Promise<void> => {
    const response = await makeRequest(`/api/columns/${columnId}`, {
      method: 'DELETE',
    });
    return handleApiResponse<void>(response);
  },

  reorder: async (
    projectId: string,
    columnIds: string[]
  ): Promise<ProjectColumn[]> => {
    const response = await makeRequest(
      `/api/projects/${projectId}/columns/reorder`,
      {
        method: 'POST',
        body: JSON.stringify({ column_ids: columnIds }),
      }
    );
    return handleApiResponse<ProjectColumn[]>(response);
  },
};
